import { Badge, Button, Input, SimpleGrid, Text } from "@mantine/core";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIsOnline, usePostApi } from "src/hooks";
import styles from "src/pages/Settings/AccountDetails/AccountDetails.module.scss";
import {
  AccountTitleWrapper,
  ButtonWrapper,
  FieldsSection,
  FirstUserSettingsContainer,
  UserName,
} from "src/pages/Settings/AccountDetails/AccountDetails.styled";
import { type IAccountDetailsForm } from "src/pages/Settings/AccountDetails/AccountDetails.types";
import { Notification } from "src/utils/Notification";

interface Props {
  titleStrings: string[];
  rolename: string;
  formData: IAccountDetailsForm;
  refetch: () => void;
}

const AccountDetailsForm = ({
  titleStrings,
  rolename,
  formData,
  refetch,
}: Props) => {
  const { post, isLoading } = usePostApi();
  const isOnline = useIsOnline();

  const { register, handleSubmit, reset } = useForm<IAccountDetailsForm>({
    defaultValues: formData,
  });

  const onSubmit: SubmitHandler<IAccountDetailsForm> = async data => {
    await post({
      request: "user-save-account",
      body: { params: data },
      onSuccess: () => {
        Notification(
          "Success",
          "Account details have been successfully changed.",
          "green",
        );
        reset(data);
        refetch?.();
      },
    });
  };

  return (
    <FirstUserSettingsContainer
      className={styles.compactUserSettings}
      onSubmit={handleSubmit(onSubmit)}>
      <UserName>
        <AccountTitleWrapper>
          {titleStrings.map(e => (
            <Text key={e} component="span" className={styles.sectionTitle}>
              {e}
            </Text>
          ))}
        </AccountTitleWrapper>
        <Badge variant="light" className={styles.rankBadge}>
          {rolename}
        </Badge>
      </UserName>
      <FieldsSection>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          <Input.Wrapper label="First Name">
            <Input {...register("fname")} readOnly={!isOnline} />
          </Input.Wrapper>
          <Input.Wrapper label="Surname">
            <Input {...register("sname")} readOnly={!isOnline} />
          </Input.Wrapper>
          <Input.Wrapper label="Email">
            <Input {...register("email")} readOnly={!isOnline} />
          </Input.Wrapper>
          <Input.Wrapper label="Personal Email">
            <Input {...register("personalEmail")} readOnly={!isOnline} />
          </Input.Wrapper>
          <Input.Wrapper label="Phone Number">
            <Input {...register("phoneno")} readOnly={!isOnline} />
          </Input.Wrapper>
          <Input.Wrapper label="Mobile">
            <Input {...register("mobile")} readOnly={!isOnline} />
          </Input.Wrapper>
        </SimpleGrid>
      </FieldsSection>
      {isOnline && (
        <ButtonWrapper>
          <Button
            type="submit"
            variant="outline"
            size="xs"
            mt="sm"
            loading={isLoading}>
            Save
          </Button>
        </ButtonWrapper>
      )}
    </FirstUserSettingsContainer>
  );
};

export default AccountDetailsForm;
